import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from '@ohif/ui';
import { AppDispatch, RootState } from '../store/store';
import {
  getDistributionHistoryByStudyApi,
  DistributionRecord,
} from '../rtk-apis/hl7/getDistributionHistory';
import { sendDraftDistributionApi } from '../rtk-apis/hl7/sendDraftDistribution';
import { clearSendDraftDistributionState } from '../store/slices/hl7/sendDraftDistribution';
import '../styled/pages/dashboard.scss';

export interface QuickDistributePatientInfo {
  name?: string;
  patientName?: string;
  dob?: string;
  reportName?: string;
  date?: string;
  time?: string;
  patientId?: string;
}

interface QuickDistributePopupProps {
  studyInstanceUID: string;
  patientInfo: QuickDistributePatientInfo;
  onClose: () => void;
  onSent?: () => void;
}

const QuickDistributePopup: React.FC<QuickDistributePopupProps> = ({
  studyInstanceUID,
  patientInfo,
  onClose,
  onSent,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [drafts, setDrafts] = useState<DistributionRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sendingId, setSendingId] = useState<string | null>(null);

  const { status: sendStatus, msg: sendMsg } = useSelector(
    (state: RootState) =>
      state.sendDraftDistributionResponse || { status: '', msg: '' }
  );

  const loadDrafts = useCallback(async () => {
    if (!studyInstanceUID) return;
    setLoading(true);
    setError(null);
    try {
      const resultAction = await dispatch(
        getDistributionHistoryByStudyApi({ studyInstanceUID, page: 1, limit: 100 })
      );
      if (getDistributionHistoryByStudyApi.fulfilled.match(resultAction)) {
        const all = resultAction.payload?.data ?? [];
        setDrafts(all.filter(d => d.status === 'DRAFT'));
      } else {
        setError((resultAction.payload as string) || 'Failed to load distributions');
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load distributions');
    } finally {
      setLoading(false);
    }
  }, [studyInstanceUID, dispatch]);

  useEffect(() => {
    dispatch(clearSendDraftDistributionState());
  }, [dispatch]);

  useEffect(() => {
    loadDrafts();
  }, [loadDrafts]);

  useEffect(() => {
    if (sendStatus === 'SUCCESS') {
      dispatch(clearSendDraftDistributionState());
      loadDrafts();
      onSent?.();
    }
  }, [sendStatus, dispatch, loadDrafts, onSent]);

  const displayName =
    patientInfo.name || patientInfo.patientName || '—';

  const handleSend = async (distributionId: string) => {
    setSendingId(distributionId);
    try {
      await dispatch(sendDraftDistributionApi({ distributionId })).unwrap();
    } catch {
      // msg surfaced via selector if needed
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div className="modal modal--open">
      <div className="modal__dialog" style={{ width: '50%', maxWidth: '600px' }}>
        <div
          className="modal__content modal__content--lg"
          style={{
            width: '100%',
            maxWidth: '600px',
            maxHeight: '90vh',
            alignItems: 'stretch',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div className="modal__header" style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', width: '100%' }}>
            <h1 className="modal__header--title">Distribute</h1>
            <button type="button" className="btn btn__transparent btnClose" onClick={onClose} aria-label="Close">
              <Icon name="close" />
            </button>
          </div>

          <div
            className="modal__body modal-body-left"
            style={{
              overflowY: 'auto',
              overflowX: 'hidden',
              flex: '1 1 auto',
              minHeight: 0,
              width: '100%',
            }}
          >
            <div
              style={{
                width: '100%',
                marginBottom: '1rem',
                padding: '1rem',
                border: '1px solid rgba(255, 255, 255, 0.85)',
                borderRadius: '0.5rem',
              }}
            >
              <p className="modal__body--title" style={{ marginBottom: '1rem', width: '100%', textAlign: 'left' }}>
                Patient & report
              </p>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: '1.25rem 2rem',
                }}
              >
                <div className="min-w-0">
                  <div className="form__label" style={{ marginBottom: '0.25rem' }}>
                    Patient
                  </div>
                  <div className="modal__body--message" style={{ fontSize: '1rem', fontWeight: 600, textAlign: 'left' }}>
                    {displayName}
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="form__label" style={{ marginBottom: '0.25rem' }}>
                    DOB
                  </div>
                  <div className="modal__body--message" style={{ fontSize: '1rem', fontWeight: 600, textAlign: 'left' }}>
                    {patientInfo.dob || '—'}
                  </div>
                </div>
                {patientInfo.reportName && (
                  <div className="min-w-0">
                    <div className="form__label" style={{ marginBottom: '0.25rem' }}>
                      Report
                    </div>
                    <div className="modal__body--message" style={{ fontSize: '1rem', fontWeight: 600, textAlign: 'left' }}>
                      {patientInfo.reportName}
                    </div>
                  </div>
                )}
                {(patientInfo.date || patientInfo.time) && (
                  <div className="min-w-0">
                    <div className="form__label" style={{ marginBottom: '0.25rem' }}>
                      Date
                    </div>
                    <div className="modal__body--message" style={{ fontSize: '1rem', fontWeight: 600, textAlign: 'left' }}>
                      {[patientInfo.date, patientInfo.time].filter(Boolean).join(' ')}
                    </div>
                  </div>
                )}
                {patientInfo.patientId && (
                  <div className="min-w-0">
                    <div className="form__label" style={{ marginBottom: '0.25rem' }}>
                      MRN
                    </div>
                    <div className="modal__body--message" style={{ fontSize: '1rem', fontWeight: 600, textAlign: 'left' }}>
                      {patientInfo.patientId}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {error && (
              <p className="para" style={{ marginBottom: '0.5rem', color: '#f87171', textAlign: 'left', width: '100%' }}>
                {error}
              </p>
            )}
            {sendStatus === 'FAILED' && sendMsg && (
              <p className="para" style={{ marginBottom: '0.5rem', color: '#f87171', textAlign: 'left', width: '100%' }}>
                {sendMsg}
              </p>
            )}

            {loading ? (
              <p className="modal__header--para" style={{ width: '100%', textAlign: 'left' }}>
                Loading drafts…
              </p>
            ) : drafts.length === 0 ? (
              <p className="modal__header--para" style={{ width: '100%', textAlign: 'left' }}>
                No distributions prepared. Go to the Distribution page to add recipients.
              </p>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, width: '100%', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {drafts.map(d => (
                  <li
                    key={d._id}
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '0.5rem',
                      padding: '0.75rem',
                      border: '1px solid rgba(255, 255, 255, 0.35)',
                      borderRadius: '0.5rem',
                    }}
                  >
                    <div className="min-w-0" style={{ flex: '1 1 200px' }}>
                      {/* An email draft has no sending provider and its recipient is an
                          address, so the MO fields would both render as a dash. */}
                      <span className="modal__header--para">From </span>
                      <span className="modal__body--message" style={{ fontSize: '0.9375rem' }}>
                        {d.type === 'email'
                          ? 'No-reply email'
                          : d.fromProviderName || d.fromProviderId || '—'}
                      </span>
                      <span className="modal__header--para"> → To </span>
                      <span className="modal__body--message" style={{ fontSize: '0.9375rem', wordBreak: 'break-word' }}>
                        {d.type === 'email'
                          ? d.toEmail || d.toProviderName || '—'
                          : d.toProviderName || d.toProviderId || '—'}
                      </span>
                    </div>
                    <button
                      type="button"
                      disabled={sendingId === d._id}
                      onClick={() => handleSend(d._id)}
                      className="btn btn__primary"
                      style={{ minHeight: '32px', padding: '0.25rem 0.75rem', fontSize: '0.8125rem', lineHeight: '18px' }}
                    >
                      {sendingId === d._id ? 'Sending…' : 'Send'}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="modal__footer" style={{ width: '100%' }}>
            <div className="btn__group" style={{ justifyContent: 'flex-end' }}>
              <button type="button" onClick={onClose} className="btn btn__white btn__outlined">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal__backdrop" aria-hidden />
    </div>
  );
};

export default QuickDistributePopup;
